import { EyeOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Descriptions, message, Modal, Select, Space, Table, TableColumnsType, Tag, Input, Tour, ConfigProvider } from 'antd';
import type { TourProps } from 'antd';
import axios from 'axios';
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { configService } from '../services/configService';
import { orderService } from '../services/orderService';
import { userService } from '../services/userService';
import type { Order, OrderItem } from '../types/order';
import { useNavigate } from 'react-router-dom';
import { coffeeService } from '../services/coffeeService';
import { bottledDrinkService } from '../services/bottledDrinkService';
import { DishService } from '../services/dishService';
import AdminLayout from '../components/AdminLayout';
import '../styles/AdminPages.css';
import viVN from 'antd/locale/vi_VN';

const { Search } = Input;

const dishService = new DishService();

const getStatusColor = (status: string) => {
    switch (status) {
        case 'waiting':
            return 'gold';
        case 'confirmed':
            return 'blue';
        case 'shipping':
            return 'purple';
        case 'delivered':
            return 'green';
        case 'paid':
            return 'cyan';
        case 'cancelled':
            return 'red';
        default:
            return 'default';
    }
};

const getStatusText = (status: string) => {
    switch (status) {
        case 'waiting':
            return 'Đang chờ xác nhận';
        case 'confirmed':
            return 'Đã xác nhận';
        case 'shipping':
            return 'Đang giao hàng';
        case 'delivered':
            return 'Giao hàng thành công';
        case 'paid':
            return 'Đã thanh toán';
        case 'cancelled':
            return 'Đã hủy đơn';
        default:
            return status;
    }
};

const getStatusTextMessage = (status: string) => {
    switch (status) {
        case 'waiting':
            return 'Đơn hàng đang chờ xác nhận';
        case 'confirmed':
            return 'Đơn hàng đã xác nhận';
        case 'shipping':
            return 'Đơn hàng đang được giao';
        case 'delivered':
            return 'Đơn hàng đã giao thành công';
        case 'paid':
            return 'Đơn hàng đã thanh toán';
        case 'cancelled':
            return 'Đơn hàng đã bị hủy';
        default:
            return status;
    }
};

const OrderManagement: React.FC = () => {
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(false);
    const [detailsModalVisible, setDetailsModalVisible] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<any>(null);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const isMobile = windowWidth <= 400;
    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const navigate = useNavigate();
    const userCacheRef = useRef<Map<string, any>>(new Map());
    const zaloConfigRef = useRef<any>(null);
    const fetchOrdersRequestIdRef = useRef(0);
    const isMountedRef = useRef(true);
    const searchDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Tour state and refs
    const [isTourOpen, setIsTourOpen] = useState(false);
    const titleRef = useRef(null);
    const searchRef = useRef(null);
    const filtersRef = useRef(null);
    const tableContainerRef = useRef(null);

    const tourSteps: TourProps['steps'] = useMemo(() => [
        {
            title: 'Quản lý đơn hàng',
            description: 'Chào mừng bạn đến với trang Quản lý đơn hàng! Đây là nơi bạn có thể theo dõi và quản lý tất cả các đơn hàng của khách hàng, từ lúc đặt hàng đến khi hoàn thành giao hàng.',
            target: () => titleRef.current,
        },
        {
            title: 'Tìm kiếm',
            description: 'Tìm kiếm nhanh đơn hàng theo tên khách hàng hoặc số điện thoại. Nhập từ khóa và kết quả sẽ được lọc ngay lập tức.',
            target: () => searchRef.current,
        },
        {
            title: 'Bộ lọc trạng thái',
            description: 'Lọc đơn hàng theo trạng thái như đang chờ xác nhận, đã xác nhận, đang giao hàng, giao hàng thành công, đã thanh toán hoặc đã hủy.',
            target: () => filtersRef.current,
        },
        {
            title: 'Bảng dữ liệu đơn hàng',
            description: 'Bảng hiển thị tất cả các đơn hàng với thông tin chi tiết như mã đơn hàng, khách hàng, sản phẩm, trạng thái, tổng tiền và ngày đặt. Bạn có thể xem chi tiết và cập nhật trạng thái từng đơn hàng.',
            target: () => tableContainerRef.current,
        },
        {
            title: 'Kết thúc hướng dẫn!',
            description: 'Cảm ơn bạn đã xem hướng dẫn này. Bây giờ bạn đã sẵn sàng quản lý đơn hàng hiệu quả. Bạn có thể theo dõi, xử lý và cập nhật trạng thái các đơn hàng một cách chuyên nghiệp.',
            target: () => titleRef.current,
        },
    ], []);

    const selectedPaymentMethod = useMemo(() => {
        if (!selectedOrder?.paymentMethod) return '';
        if (selectedOrder.paymentMethod === 'COD') return 'Thanh toán khi nhận hàng';
        if (selectedOrder.paymentMethod === 'BANK') return 'Thanh toán qua ngân hàng';
        if (selectedOrder.paymentMethod === 'ZALOPAY') return 'Thanh toán qua ZALOPAY';
        return selectedOrder.paymentMethod;
    }, [selectedOrder]);

    const getZaloConfig = useCallback(async () => {
        if (zaloConfigRef.current) {
            return zaloConfigRef.current;
        }
        const config = await configService.getConfig();
        zaloConfigRef.current = config;
        return config;
    }, []);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (searchDebounceTimerRef.current) {
                clearTimeout(searchDebounceTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (selectedOrder) {
            setDetailsModalVisible(true);
        }
    }, [selectedOrder]);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (searchDebounceTimerRef.current) {
            clearTimeout(searchDebounceTimerRef.current);
        }

        searchDebounceTimerRef.current = setTimeout(() => {
            setDebouncedSearchText(searchText.trim());
        }, 300);

        return () => {
            if (searchDebounceTimerRef.current) {
                clearTimeout(searchDebounceTimerRef.current);
            }
        };
    }, [searchText]);

    const fetchOrders = useCallback(async () => {
        const requestId = ++fetchOrdersRequestIdRef.current;
        setLoading(true);
        try {
            const data = await orderService.getAllOrders();

            const uniqueUserIds = Array.from(new Set(data.map(order => order.userId).filter(Boolean)));
            const missingUserIds = uniqueUserIds.filter(userId => !userCacheRef.current.has(userId));

            if (missingUserIds.length > 0) {
                const users = await Promise.all(
                    missingUserIds.map(async (userId) => {
                        const user = await userService.getUserById(userId);
                        return { userId, user };
                    })
                );

                users.forEach(({ userId, user }) => {
                    userCacheRef.current.set(userId, user);
                });
            }

            const ordersWithUserDetails = data.map((order) => ({
                ...order,
                user: userCacheRef.current.get(order.userId) || null
            }));

            if (!isMountedRef.current || requestId !== fetchOrdersRequestIdRef.current) {
                return;
            }

            setOrders(ordersWithUserDetails);
        } catch (error) {
            console.error('Error fetching orders:', error);
            message.error('Không thể tải danh sách đơn hàng');
        } finally {
            if (isMountedRef.current && requestId === fetchOrdersRequestIdRef.current) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    const handleUpdateStatus = useCallback(async (orderId: string, status: string) => {
        try {
            await orderService.updateOrderStatus(orderId, status as any);
            message.success('Cập nhật trạng thái đơn hàng thành công');

            const currentOrder = orders.find(order => order.id === orderId) as any;
            const order: any = currentOrder || await orderService.getOrderById(orderId);

            if (!order) {
                message.error('Không tìm thấy thông tin đơn hàng');
                return;
            }

            // Nếu trạng thái là đã thanh toán, cập nhật số lượng mua cho các sản phẩm
            if (status === 'paid' && currentOrder?.status !== 'paid') {
                // Xử lý cập nhật purchaseCount cho từng sản phẩm trong đơn hàng
                for (const item of order.items) {
                    if (item.coffeeId) {
                        // Lấy thông tin cà phê hiện tại
                        const coffee = await coffeeService.getCoffeeById(item.coffeeId);
                        if (coffee) {
                            // Cập nhật purchaseCount
                            const currentPurchaseCount = coffee.purchaseCount || 0;
                            await coffeeService.updateCoffee(item.coffeeId, {
                                purchaseCount: currentPurchaseCount + item.quantity
                            });
                        }
                    }
                    if (item.drinkId) {
                        // Lấy thông tin đồ uống hiện tại
                        const drink = await bottledDrinkService.getBottledDrinkById(item.drinkId);
                        if (drink) {
                            // Cập nhật purchaseCount
                            const currentPurchaseCount = drink.purchaseCount || 0;
                            await bottledDrinkService.updateBottledDrink(item.drinkId, {
                                purchaseCount: currentPurchaseCount + item.quantity
                            });
                        }
                    }
                    if (item.dishId) {
                        // Lấy thông tin đồ uống hiện tại
                        const dish = await dishService.getDishById(item.dishId);
                        if (dish) {
                            // Cập nhật purchaseCount
                            const currentPurchaseCount = dish.purchaseCount || 0;
                            await dishService.updateDish(item.dishId, {
                                purchaseCount: currentPurchaseCount + item.quantity
                            });
                        }
                    }
                }
            }

            const userId = order.userId;

            const newConfigZalo = await getZaloConfig();

            let user = currentOrder?.user || userCacheRef.current.get(userId) || null;
            if (!user && userId) {
                user = await userService.getUserById(userId);
                userCacheRef.current.set(userId, user);
            }

            let userDetail: any = null;
            const userIdForZalo = user?.zaloUserId || user?.localId;
            if (newConfigZalo?.access_token_zalo && userIdForZalo) {
                userDetail = await axios.get(`https://openapi.zalo.me/v3.0/oa/user/detail?data={"user_id":"${userIdForZalo}"}`, {
                    headers: {
                        'access_token': newConfigZalo?.access_token_zalo,
                        'Content-Type': 'application/json'
                    }
                });
            }

            // Tạo nội dung tin nhắn hóa đơn

            const orderItems = order.items.length === 1
                ? order.items[0].coffeeId
                    ? `- (Coffee) ${order.items[0].name}  - ${order.items[0].weight}g - ${order.items[0].grindType === 'whole' ? 'Nguyên hạt' : 'Xay sẵn'} ${order.items[0].grindSize ? `- ${order.items[0].grindSize}` : ''} - ${order.items[0].price.toLocaleString()}đ (${order.items[0].quantity} sản phẩm)`
                    : order.items[0].drinkId
                        ? `- (Đồ uống) ${order.items[0].name} - ${order.items[0].volume}ml - ${order.items[0].price.toLocaleString()}đ (${order.items[0].quantity} sản phẩm)`
                        : order.items[0].dishId
                            ? `- (Đồ uống) ${order.items[0].name} - ${order.items[0].price.toLocaleString()}đ (${order.items[0].quantity} sản phẩm)`
                            : `- ${order.items[0].name} - ${order.items[0].price.toLocaleString()}đ (${order.items[0].quantity} sản phẩm)`
                : order.items.map((item: any) =>
                    item.coffeeId
                        ? `- (Coffee) ${item.name}  - ${item.weight}g - ${item.grindType === 'whole' ? 'Nguyên hạt' : 'Xay sẵn'} ${item.grindSize ? `- ${item.grindSize}` : ''} - ${item.price.toLocaleString()}đ (${item.quantity} sản phẩm)`
                        : item.drinkId
                            ? `- (Đồ uống) ${item.name} - ${item.volume}ml - ${item.price.toLocaleString()}đ (${item.quantity} sản phẩm)`
                            : item.dishId
                                ? `- (Đồ uống) ${item.name} - ${item.price.toLocaleString()}đ (${item.quantity} sản phẩm)`
                                : `- ${item.name} - ${item.price.toLocaleString()}đ (${item.quantity} sản phẩm)`
                ).join('\n');

            const textChangeStatus = getStatusTextMessage(status);

            // Lấy danh sách userId để gửi thông báo từ config
            const adminUserIds = newConfigZalo?.userIdMessage ?
                (Array.isArray(newConfigZalo.userIdMessage) ?
                    newConfigZalo.userIdMessage :
                    newConfigZalo.userIdMessage.split(',').map((id: string) => id.trim())
                ) : [];

            let messageText = '';
            let messageTextToUser = '';
            let totalAmountWithShipping = 0;
            if (order.paymentMethod === 'COD' && order.shippingFee) {
                totalAmountWithShipping = order.totalAmount + order.shippingFee;
                messageText = `Mã đơn hàng: ${orderId} \nĐơn hàng: \n${orderItems} \nTổng tiền: ${order.totalAmount.toLocaleString()}đ \nPhí vận chuyển: ${order.shippingFee.toLocaleString()}đ \nTổng tiền cần thanh toán: ${totalAmountWithShipping.toLocaleString()}đ \nTên khách hàng: ${order.shippingInfo.fullName} \nSố điện thoại: ${order.shippingInfo.phone} \nĐịa chỉ: ${order.shippingInfo.address}, ${order.shippingInfo.ward}, ${order.shippingInfo.district}, ${order.shippingInfo.province} \nPhương thức thanh toán: ${order.paymentMethod} \nTrạng thái: ${textChangeStatus}`
                messageTextToUser = `Mã đơn hàng: ${orderId} \nĐơn hàng: \n${orderItems} \nTổng tiền: ${order.totalAmount.toLocaleString()}đ \nPhí vận chuyển: ${order.shippingFee.toLocaleString()}đ \nTổng tiền cần thanh toán: ${totalAmountWithShipping.toLocaleString()}đ \nPhương thức thanh toán: ${order.paymentMethod} \nTrạng thái: ${textChangeStatus}`
            }

            else {
                messageText = `Mã đơn hàng: ${orderId} \nĐơn hàng: \n${orderItems} \nTổng tiền: ${order.totalAmount.toLocaleString()}đ \nTên khách hàng: ${order.shippingInfo.fullName} \nSố điện thoại: ${order.shippingInfo.phone} \nĐịa chỉ: ${order.shippingInfo.address}, ${order.shippingInfo.ward}, ${order.shippingInfo.district}, ${order.shippingInfo.province} \nPhương thức thanh toán: ${order.paymentMethod} \nTrạng thái: ${textChangeStatus}`
                messageTextToUser = `Mã đơn hàng: ${orderId} \nĐơn hàng: \n${orderItems} \nTổng tiền: ${order.totalAmount.toLocaleString()}đ \nPhương thức thanh toán: ${order.paymentMethod} \nTrạng thái: ${textChangeStatus}`
            }

            // Gửi tin nhắn cho danh sách admin users
            for (const adminUserId of adminUserIds) {
                try {
                    await axios.post('https://openapi.zalo.me/v3.0/oa/message/cs', {
                        recipient: {
                            user_id: adminUserId
                        },
                        message: {
                            "text": messageText,
                            "attachment": {
                                "type": "template",
                                "payload": {
                                    "buttons": [
                                        {
                                            "title": "Xem đơn hàng",
                                            "type": "oa.open.url",
                                            "image_icon": "https://png.pngtree.com/png-clipart/20230418/original/pngtree-order-confirm-line-icon-png-image_9065104.png",
                                            "payload": {
                                                "url": `https://coffee.updates.com.vn/order/${orderId}`
                                            }
                                        }
                                    ]
                                }
                            }
                        }
                    }, {
                        headers: {
                            'access_token': newConfigZalo?.access_token_zalo,
                            'Content-Type': 'application/json'
                        }
                    });
                } catch (error) {
                    console.error(`Lỗi khi gửi tin nhắn cho admin ${adminUserId}:`, error);
                }
            }

            // Gửi tin nhắn cho người dùng đặt hàng
            try {
                const receiverUserId = userDetail?.data?.user_id || user?.localId;
                if (receiverUserId) {
                    await axios.post('https://openapi.zalo.me/v3.0/oa/message/cs', {
                        recipient: {
                            user_id: receiverUserId
                        },
                        message: {
                            "text": messageTextToUser,
                            "attachment": {
                                "type": "template",
                                "payload": {
                                    "buttons": [
                                        {
                                            "title": "Mở mini app",
                                            "type": "oa.open.url",
                                            "payload": {
                                                "url": `https://zalo.me/s/1410152383611769410`
                                            }
                                        }
                                    ]
                                }
                            }
                        }
                    }, {
                        headers: {
                            'access_token': newConfigZalo?.access_token_zalo,
                            'Content-Type': 'application/json'
                        }
                    });
                }
            } catch (error) {
                console.error('Lỗi khi gửi tin nhắn cho khách hàng:', error);
            }

            setOrders((prevOrders) => prevOrders.map((item: any) =>
                item.id === orderId
                    ? { ...item, status }
                    : item
            ));

            setSelectedOrder({
                ...order,
                status,
                user: order.user || user || currentOrder?.user || null
            });

            // Gọi api gửi dữ liệu đơn hàng để AI tự động cập nhật
            // await axios.get(`https://coffee-ai.8am.vn/update`);
        } catch (error) {
            console.error('Error updating order status:', error);
            message.error('Không thể cập nhật trạng thái đơn hàng');
        }
    }, [orders, getZaloConfig]);

    const handleViewDetails = useCallback((order: any) => {
        navigate(`/order/${order.id}`);
    }, [navigate]);

    const filteredOrders = useMemo(() => {
        const normalizedSearchText = debouncedSearchText.toLowerCase();
        return orders.filter(order => {
            const matchesSearch = !normalizedSearchText
                || order.user?.name?.toLowerCase().includes(normalizedSearchText)
                || order.user?.phoneNumber?.includes(debouncedSearchText);
            const matchesStatus = statusFilter ? order.status === statusFilter : true;
            return matchesSearch && matchesStatus;
        });
    }, [orders, debouncedSearchText, statusFilter]);

    const todayOrderCount = useMemo(() => {
        const today = new Date();
        return orders.filter(order => {
            const timestamp = order.createdAt;
            const orderDate = timestamp && typeof timestamp === 'object' && 'seconds' in timestamp
                ? new Date((timestamp.seconds as number) * 1000)
                : timestamp ? new Date(timestamp) : new Date();
            return orderDate.getDate() === today.getDate()
                && orderDate.getMonth() === today.getMonth()
                && orderDate.getFullYear() === today.getFullYear();
        }).length;
    }, [orders]);

    const columns: TableColumnsType<Order> = useMemo(() => [
        {
            title: 'Mã đơn hàng',
            dataIndex: 'id',
            key: 'id',
            width: 100,
        },
        {
            title: 'Người đặt',
            dataIndex: ['user', 'name'],
            key: 'userName',
            width: 150,
            render: (name: string, record: any) => (
                <span>{name || 'N/A'}</span>
            )
        },
        {
            title: 'Số điện thoại',
            dataIndex: ['user', 'phoneNumber'],
            key: 'phoneNumber',
            width: 100,
            render: (phone: string) => (
                <span>{phone?.replace('84', '0') || 'N/A'}</span>
            )
        },
        {
            title: 'Sản phẩm',
            dataIndex: 'items',
            key: 'items',
            width: 400,
            render: (items: any[]) => (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 10
                }}>
                    {items.map((item, index) => (
                        <div key={index} style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0
                        }}>
                            {item.coffeeId && (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 4
                                }}>
                                    <div>{item.name} - {item.quantity}x{item.weight}g</div>
                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                        • {item.grindType === 'ground' ? 'Xay sẵn' : 'Nguyên hạt'}
                                        {item.grindType === 'ground' && ` - Độ mịn: ${item.grindSize}`}
                                    </div>
                                </div>
                            )}
                            {item.drinkId && (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 4
                                }}>
                                    <div>{item.name} - {item.quantity}x{item.volume}ml</div>
                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                        Đồ uống đóng chai
                                    </div>
                                </div>
                            )}
                            {item.dishId && (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 4
                                }}>
                                    <div>{item.name} - {item.quantity} sản phẩm</div>
                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                        Đồ uống
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )
        },
        {
            title: 'Tiền hàng',
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            width: 100,
            render: (amount: number) => (
                <span style={{ color: '#1890ff' }}>
                    {amount.toLocaleString('vi-VN')}đ
                </span>
            )
        },
        {
            title: 'Phí ship',
            dataIndex: 'shippingFee',
            key: 'shippingFee',
            width: 100,
            render: (fee: number) => (
                <span style={{ color: '#52c41a' }}>
                    {fee ? fee.toLocaleString('vi-VN') + 'đ' : '0đ'}
                </span>
            )
        },
        {
            title: 'Tổng thanh toán',
            key: 'totalPayment',
            width: 120,
            render: (record: any) => {
                const total = record.totalAmount + (record.shippingFee || 0);
                return (
                    <span style={{ color: '#f5222d', fontWeight: 500 }}>
                        {total.toLocaleString('vi-VN')}đ
                    </span>
                );
            }
        },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            width: 70,
            render: (status: string) => (
                <Tag color={getStatusColor(status)}>
                    {getStatusText(status)}
                </Tag>
            )
        },
        {
            title: 'Ngày tạo',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 80,
            render: (date: any) => {
                if (date?.seconds) {
                    return new Date(date.seconds * 1000).toLocaleDateString('vi-VN', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                    });
                }
                return new Date(date).toLocaleDateString('vi-VN', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            }
        },
        {
            title: 'Thao tác',
            key: 'action',
            fixed: 'right',
            width: 80,
            render: (_: any, record: Order) => (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4
                }}>
                    <Button
                        icon={<EyeOutlined />}
                        onClick={() => handleViewDetails(record)}
                        style={{ width: 'fit-content' }}
                    >
                        Chi tiết
                    </Button>
                    <Select
                        value={record.status}
                        style={{ width: 150 }}
                        onChange={(value) => handleUpdateStatus(record.id!, value)}
                        disabled={record.status === 'paid' || record.status === 'cancelled'}
                    >
                        <Select.Option value="waiting">Đang chờ xác nhận</Select.Option>
                        <Select.Option value="confirmed">Đã xác nhận</Select.Option>
                        <Select.Option value="shipping">Đang giao hàng</Select.Option>
                        <Select.Option value="delivered">Giao hàng thành công</Select.Option>
                        <Select.Option value="paid">Đã thanh toán</Select.Option>
                        <Select.Option value="cancelled">Đã hủy đơn</Select.Option>
                    </Select>
                </div>
            )
        }
    ], [handleUpdateStatus, handleViewDetails]);

    const mobileColumns: TableColumnsType<any> = useMemo(() => [
        {
            title: 'Thông tin',
            dataIndex: 'id',
            key: 'info',
            render: (_, record: any) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ fontSize: '12px' }}>
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            <div>
                                <strong>Mã đơn hàng:</strong>{' '}
                                <Tag style={{ fontSize: '11px' }}>{record.id}</Tag>
                            </div>

                            <div>
                                <strong>Khách hàng:</strong>{' '}
                                <Tag style={{ fontSize: '11px' }}>
                                    {record.user?.name || record.user?.phoneNumber || 'N/A'}
                                </Tag>
                            </div>

                            <div>
                                <strong>Trạng thái:</strong>{' '}
                                <Tag color={getStatusColor(record.status)} style={{ fontSize: '11px' }}>
                                    {getStatusText(record.status)}
                                </Tag>
                            </div>

                            <div>
                                <strong>Sản phẩm:</strong>
                                <div style={{ marginTop: 4 }}>
                                    {record.items.map((item: any, index: number) => (
                                        <div key={index} style={{ fontSize: '11px', marginBottom: 4 }}>
                                            {item.coffeeId &&
                                                <div>
                                                    {item.name} - {item.quantity}x{item.weight}g
                                                </div>
                                            }
                                            {item.drinkId &&
                                                <div>
                                                    {item.name} - {item.quantity}x{item.volume}ml
                                                </div>
                                            }
                                            {item.dishId &&
                                                <div>
                                                    {item.name} - {item.quantity} sản phẩm
                                                </div>
                                            }
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <strong>Tổng tiền:</strong>{' '}
                                <Tag color="orange" style={{ fontSize: '11px' }}>
                                    {record.totalAmount.toLocaleString('vi-VN')}đ
                                </Tag>
                            </div>

                            <div>
                                <strong>Ngày đặt:</strong>{' '}
                                <span style={{ fontSize: '11px' }}>
                                    {new Date(record.createdAt.seconds * 1000).toLocaleDateString('vi-VN', {
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                </span>
                            </div>
                        </Space>
                    </div>
                </div>
            )
        },
        {
            title: 'Thao tác',
            key: 'action',
            render: (_: any, record: Order) => (
                <Space direction="vertical" size={8}>
                    <Button
                        icon={<EyeOutlined />}
                        onClick={() => handleViewDetails(record)}
                        size="small"
                    >
                        Chi tiết
                    </Button>
                    <Select
                        value={record.status}
                        style={{ width: 130 }}
                        onChange={(value) => handleUpdateStatus(record.id!, value)}
                        size="small"
                        disabled={record.status === 'paid' || record.status === 'cancelled'}
                    >
                        <Select.Option value="waiting">Đang chờ xác nhận</Select.Option>
                        <Select.Option value="confirmed">Đã xác nhận</Select.Option>
                        <Select.Option value="shipping">Đang giao hàng</Select.Option>
                        <Select.Option value="delivered">Giao hàng thành công</Select.Option>
                        <Select.Option value="paid">Đã thanh toán</Select.Option>
                        <Select.Option value="cancelled">Đã hủy đơn</Select.Option>
                    </Select>
                </Space>
            )
        }
    ], [handleUpdateStatus, handleViewDetails]);

    return (
        <AdminLayout>
            <div className="admin-page-header">
                <div className="admin-page-title">
                    <h1 ref={titleRef}>Quản lý đơn hàng</h1>
                    <Tag color="blue">{orders.length} đơn hàng</Tag>
                    <Tag color="green">{todayOrderCount} đơn hàng hôm nay</Tag>
                    <Button
                        type="primary"
                        icon={<QuestionCircleOutlined />}
                        onClick={() => setIsTourOpen(true)}
                        style={{ marginLeft: 8 }}
                    >
                        Hướng dẫn
                    </Button>
                </div>

                <div className="admin-page-actions">
                    <div className="admin-search-filters" ref={filtersRef}>
                        <div ref={searchRef}>
                            <Search
                                placeholder="Tìm theo tên hoặc số điện thoại"
                                allowClear
                                style={{ width: isMobile ? '100%' : 300 }}
                                onChange={(e) => setSearchText(e.target.value)}
                                className="admin-search-input"
                            />
                        </div>
                        <Select
                            placeholder="Lọc theo trạng thái"
                            style={{ width: isMobile ? '100%' : 200 }}
                            allowClear
                            onChange={(value) => setStatusFilter(value)}
                            className="admin-select"
                        >
                            <Select.Option value="waiting">Đang chờ xác nhận</Select.Option>
                            <Select.Option value="confirmed">Đã xác nhận</Select.Option>
                            <Select.Option value="shipping">Đang giao hàng</Select.Option>
                            <Select.Option value="delivered">Giao hàng thành công</Select.Option>
                            <Select.Option value="paid">Đã thanh toán</Select.Option>
                            <Select.Option value="cancelled">Đã hủy đơn</Select.Option>
                        </Select>
                    </div>
                </div>
            </div>

            <div className="admin-table-container" ref={tableContainerRef}>
                <Table
                    className="admin-table"
                    columns={isMobile ? mobileColumns : columns}
                    dataSource={filteredOrders}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: isMobile ? undefined : 2500 }}
                    pagination={{
                        responsive: true,
                        defaultPageSize: isMobile ? 5 : 10,
                        position: isMobile ? ['bottomCenter'] : ['bottomRight']
                    }}
                />
            </div>

            {selectedOrder && (
                <Modal
                    title="Chi tiết đơn hàng"
                    visible={detailsModalVisible}
                    onCancel={() => setDetailsModalVisible(false)}
                    footer={null}
                    width={isMobile ? '100%' : 800}
                    className="admin-modal"
                >
                    {isMobile ? (
                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            <div className="admin-form-section">
                                <h3 className="admin-form-section-title">Thông tin đơn hàng</h3>
                                <div>
                                    <strong>Mã đơn hàng:</strong>
                                    <div><Tag>{selectedOrder.id}</Tag></div>
                                </div>

                                <div>
                                    <strong>Trạng thái:</strong>
                                    <div>
                                        <Tag color={getStatusColor(selectedOrder.status)}>
                                            {getStatusText(selectedOrder.status)}
                                        </Tag>
                                    </div>
                                </div>

                                <div>
                                    <strong>Người đặt:</strong>
                                    <div><Tag>{selectedOrder?.user?.name || selectedOrder?.user?.phoneNumber || 'N/A'}</Tag></div>
                                </div>

                                <div>
                                    <strong>Số điện thoại:</strong>
                                    <div><Tag>{selectedOrder?.user?.phoneNumber || 'N/A'}</Tag></div>
                                </div>

                                {/* <div>
                                    <strong>Email:</strong>
                                    <div><Tag>{selectedOrder.shippingInfo.email || 'N/A'}</Tag></div>
                                </div> */}

                                <div>
                                    <strong>Địa chỉ giao hàng:</strong>
                                    <div style={{ fontSize: '13px', backgroundColor: '#f5f5f5', padding: '8px', borderRadius: '4px' }}>
                                        {selectedOrder.shippingInfo.address + ', ' +
                                            selectedOrder.shippingInfo.ward + ', ' +
                                            selectedOrder.shippingInfo.district + ', ' +
                                            selectedOrder.shippingInfo.province}
                                    </div>
                                </div>

                                <div>
                                    <strong>Sản phẩm:</strong>
                                    <div style={{ marginTop: '8px' }}>
                                        {selectedOrder.items.map((item: OrderItem, index: number) => (
                                            <div key={index}
                                                style={{
                                                    backgroundColor: '#f5f5f5',
                                                    padding: '8px',
                                                    borderRadius: '4px',
                                                    marginBottom: '8px'
                                                }}
                                            >
                                                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                                    <img
                                                        src={item.imageUrl}
                                                        alt={item.name}
                                                        style={{
                                                            width: '100%',
                                                            height: '150px',
                                                            objectFit: 'cover',
                                                            borderRadius: '4px'
                                                        }}
                                                    />
                                                    <div style={{ fontWeight: 'bold' }}>{item.name}</div>
                                                    <div>Số lượng: {item.quantity}</div>
                                                    {item.coffeeId ? (
                                                        <>
                                                            <div>Khối lượng: {item.weight}g</div>
                                                            <div>Loại xay: {item.grindType === 'ground' ? 'Xay sẵn' : 'Nguyên hạt'}</div>
                                                            {item.grindType === 'ground' && <div>Độ mịn: {item.grindSize}</div>}
                                                        </>
                                                    ) : item.drinkId ? (
                                                        <div>Thể tích: {item.volume}ml</div>
                                                    ) : item.dishId ? (
                                                        <div>Đồ uống</div>
                                                    ) : null}
                                                    <div style={{ color: '#f50' }}>
                                                        Giá: {item.price.toLocaleString('vi-VN')}đ
                                                    </div>
                                                </Space>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <strong>Phương thức thanh toán:</strong>
                                    <div>
                                        {selectedPaymentMethod}
                                    </div>
                                </div>

                                <div>
                                    <strong>Tổng tiền:</strong>
                                    <div>
                                        <Tag color="red" style={{ fontSize: '14px' }}>
                                            {selectedOrder.totalAmount.toLocaleString('vi-VN')}đ
                                        </Tag>
                                    </div>
                                </div>

                                {
                                    (selectedOrder.paymentMethod === 'COD' && selectedOrder.shippingFee) &&
                                    <div>
                                        <strong>Phí vận chuyển:</strong>
                                        <div>
                                            <Tag color="orange" style={{ fontSize: '14px' }}>
                                                {selectedOrder.shippingFee.toLocaleString('vi-VN')}đ
                                            </Tag>
                                        </div>
                                    </div>
                                }

                                {
                                    (selectedOrder.paymentMethod === 'COD' && selectedOrder.shippingFee) &&
                                    <div>
                                        <strong>Số tiền thanh toán:</strong>
                                        <div>
                                            <Tag color="red" style={{ fontSize: '14px' }}>
                                                {(selectedOrder.shippingFee + selectedOrder.totalAmount).toLocaleString('vi-VN')}đ
                                            </Tag>
                                        </div>
                                    </div>
                                }

                                <div>
                                    <strong>Ngày đặt:</strong>
                                    <div>
                                        <Tag>
                                            {new Date(selectedOrder.createdAt.seconds * 1000).toLocaleDateString('vi-VN', {
                                                year: 'numeric',
                                                month: '2-digit',
                                                day: '2-digit',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </Tag>
                                    </div>
                                </div>

                                <div>
                                    <strong>Ghi chú:</strong>
                                    <div style={{
                                        backgroundColor: '#f5f5f5',
                                        padding: '8px',
                                        borderRadius: '4px',
                                        fontSize: '13px'
                                    }}>
                                        {selectedOrder.note || 'Không có'}
                                    </div>
                                </div>
                            </div>
                        </Space>
                    ) : (
                        <div className="admin-form-container">
                            <div className="admin-form-section">
                                <h3 className="admin-form-section-title">Thông tin đơn hàng</h3>
                                <Descriptions bordered column={2}>
                                    <Descriptions.Item label="Mã đơn hàng">{selectedOrder.id}</Descriptions.Item>
                                    <Descriptions.Item label="Trạng thái">
                                        <Tag color={getStatusColor(selectedOrder.status)}>
                                            {getStatusText(selectedOrder.status)}
                                        </Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Người đặt">
                                        {selectedOrder?.user?.name || selectedOrder?.user?.phoneNumber || 'N/A'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Số điện thoại">
                                        {selectedOrder?.user?.phoneNumber || 'N/A'}
                                    </Descriptions.Item>
                                    {/* <Descriptions.Item label="Email" span={2}>
                                        {selectedOrder.shippingInfo.email || 'N/A'}
                                    </Descriptions.Item> */}
                                    <Descriptions.Item label="Địa chỉ giao hàng" span={2}>
                                        {selectedOrder.shippingInfo.address + ', ' + selectedOrder.shippingInfo.ward + ', ' + selectedOrder.shippingInfo.district + ', ' + selectedOrder.shippingInfo.province}
                                    </Descriptions.Item>

                                    <Descriptions.Item label="Sản phẩm" span={2}>
                                        {selectedOrder.items.map((item: OrderItem, index: number) => (
                                            <div key={index}
                                                style={{
                                                    backgroundColor: '#f0f0f0',
                                                    padding: '10px',
                                                    borderRadius: '5px',
                                                    marginBottom: '10px',
                                                    display: 'flex',
                                                    gap: '10px'
                                                }}
                                            >
                                                <div>
                                                    <img src={item.imageUrl} alt={item.name} style={{ width: '75px', height: '75px', objectFit: 'cover', borderRadius: '5px' }} />
                                                </div>
                                                <div>
                                                    <div>
                                                        {item.name} - {item.quantity}x
                                                        {item.coffeeId ? `${item.weight}g` :
                                                            item.drinkId ? `${item.volume}ml` :
                                                                item.dishId ? ' sản phẩm' : ''} - {item.price.toLocaleString('vi-VN')}đ
                                                    </div>
                                                    {
                                                        item.coffeeId &&
                                                        <div>
                                                            Loại xay: {item.grindType === 'ground' ? 'Xay sẵn' : 'Nguyên hạt'}
                                                            {item.grindType === 'ground' && <div>Độ mịn: {item.grindSize}</div>}
                                                        </div>
                                                    }
                                                    {
                                                        item.drinkId &&
                                                        <div>
                                                            Thể tích: {item.volume}ml
                                                        </div>
                                                    }
                                                    {
                                                        item.dishId &&
                                                        <div>
                                                            Đồ uống
                                                        </div>
                                                    }
                                                </div>
                                            </div>
                                        ))}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Phương thức thanh toán">
                                        {selectedPaymentMethod ? selectedPaymentMethod : 'COD'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Tổng tiền">
                                        {selectedOrder.totalAmount.toLocaleString('vi-VN')}đ
                                    </Descriptions.Item>
                                    {
                                        (selectedOrder.paymentMethod === 'COD' && selectedOrder.shippingFee) &&
                                        <Descriptions.Item label="Phí vận chuyển">
                                            {selectedOrder.shippingFee.toLocaleString('vi-VN')}đ
                                        </Descriptions.Item>
                                    }
                                    {
                                        (selectedOrder.paymentMethod === 'COD' && selectedOrder.shippingFee) &&
                                        <Descriptions.Item label="Số tiền phải trả">
                                            {(selectedOrder.shippingFee + selectedOrder.totalAmount).toLocaleString('vi-VN')}đ
                                        </Descriptions.Item>
                                    }
                                    <Descriptions.Item label="Ngày đặt">
                                        {new Date(selectedOrder.createdAt.seconds * 1000).toLocaleDateString('vi-VN', {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Ghi chú" span={2}>
                                        {selectedOrder.note || 'Không có'}
                                    </Descriptions.Item>
                                </Descriptions>
                            </div>
                        </div>
                    )}
                </Modal>
            )}

            {/* Tour component */}
            <ConfigProvider locale={viVN}>
                <Tour
                    open={isTourOpen}
                    onClose={() => setIsTourOpen(false)}
                    steps={tourSteps}
                    indicatorsRender={(current, total) => (
                        <span>{current + 1} / {total}</span>
                    )}
                    type="primary"
                    arrow={true}
                    placement="bottom"
                    mask={{
                        style: {
                            boxShadow: 'inset 0 0 15px #333',
                        },
                    }}
                />
            </ConfigProvider>
        </AdminLayout>
    );
};

export default OrderManagement;